<template>
  <button class="base-button"
          :class="classes"
          :type="type"
          :disabled="isDisabled"
          @click="$emit('click', $event)">
    <div v-show="!loading"
         class="content">
      <slot></slot>
    </div>

    <base-loader-dots v-show="loading"
                      v-bind="loaderOptions" />
  </button>
</template>

<script>
  export default {
    props: {
      type: {
        required: false,
        type: String,
        default: 'button'
      },

      color: {
        required: false,
        type: String,
        default: 'primary'
      },

      disabled: {
        required: false,
        type: Boolean
      },

      loading: {
        required: false,
        type: Boolean
      },

      rounded: {
        required: false,
        default: true
      }
    },

    computed: {
      isDisabled() {
        return this.disabled || this.loading
      },

      classes() {
        let classes = [
          { 'is-loading': this.loading },
          { 'is-rounded': this.rounded }
        ]

        let additionalClasses = {}
        additionalClasses[this.color] = true
        classes.push(additionalClasses)

        return classes
      },

      loaderOptions() {
        let loaderColor = null

        switch (this.color) {
          case 'primary':
          case 'secondary':
            loaderColor = '#fff'
            break
          case 'light':
            loaderColor = '#3e3a3b'
            break;
          default: {
            loaderColor = '#3e3a3b'
          }
        }

        return {
          color: loaderColor
        }
      }
    }
  }
</script>

<style lang="scss" scoped>
  .base-button {
    display: flex;
    justify-content: center;
    align-items: center;

    flex-shrink: 0; // Todo: remove?

    padding: 10px 20px;
    font-size: 18px;
    font-weight: normal;
    line-height: 1;
    border: none;
    border-radius: 2px;
    cursor: pointer;
    transition: all .1s ease-out;

    .content {
      display: flex;
      align-items: center;

      .icon-left {
        margin-right: 8px;
      }

      .icon-right {
        margin-left: 8px;
      }
    }

    &:disabled {
      cursor: default;
    }

    &.is-rounded {
      border-radius: 38px;
    }

    &.primary {
      background-color: map-get($colors, primary);
      color: #fff;

      &:hover {
        background-color: darken(map-get($colors, primary), 10%);
      }

      &:active {
        background-color: darken(map-get($colors, primary), 20%);
      }

      &:disabled {
        background-color: lighten(map-get($colors, primary), 10%);
      }
    }

    &.secondary {
      background-color: map-get($colors, secondary);
      color: #3e3a3b;

      &:hover {
        background-color: darken(map-get($colors, secondary), 10%);
      }

      &:active {
        background-color: darken(map-get($colors, secondary), 20%);
      }

      &:disabled {
        background-color: lighten(map-get($colors, secondary), 10%);
      }
    }

    &.success {
      background-color: map-get($colors, success);
      color: #fff;

      &:hover {
        background-color: darken(map-get($colors, success), 10%);
      }

      &:active {
        background-color: darken(map-get($colors, success), 20%);
      }

      &:disabled {
        background-color: lighten(map-get($colors, success), 10%);
      }
    }

    &.error {
      background-color: map-get($colors, error);
      color: #fff;

      &:hover {
        background-color: darken(map-get($colors, error), 10%);
      }

      &:active {
        background-color: darken(map-get($colors, error), 20%);
      }

      &:disabled {
        background-color: lighten(map-get($colors, error), 10%);
      }
    }

    &.light {
      background-color: #f1f0f9;
      color: #3e3a3b;

      &:hover {
        background-color: darken(#f1f0f9, 10%);
      }

      &:active {
        background-color: darken(#f1f0f9, 20%);
      }

      &:disabled {
        background-color: lighten(#f1f0f9, 10%);
      }
    }

    &.transparent {
      width: 100%;
      color: map-get($colors, primary);
      background: transparent;
      background: rgba(124, 101, 181, .15);

      &:active {
        background: rgba(124, 101, 181, .3);
      }

      &:disabled {
        color: map-get($colors, disabled);
        background: lighten(map-get($colors, disabled), 20%);
      }
    }
  }
</style>
